import styled from "styled-components";
import {motion, radii, zIndex} from "theme/tokens";

/**
 * The always-present consent-withdrawal control (kitecorp/transferify-ui#2675).
 *
 * Four choices are deliberate:
 *
 * - **Bottom-left, `position: fixed`.** Bottom-right is taken on the one surface where space is
 *   sold: a sponsor's wallpaper poster puts the advertiser's only link there (ui#1866). Mid-left is
 *   the feedback tab's (ui#1085). This corner is free on every surface, which is what lets the
 *   control keep its Art. 7(3) promise of being on every page without displacing anything.
 * - **`zIndex.nav`, not a new layer.** It is in-page chrome on the same rung as the floating top
 *   bar, so the dialog it opens (`modal`, 1300) and that dialog's scrim always cover it. No
 *   per-dialog hiding logic to keep in step.
 * - **Every viewport, unlike the feedback tab.** That tab is desktop-only because a permanently
 *   docked control on a phone obstructs the content it floats on, and Resources → Support is a fine
 *   fallback for it. Neither argument transfers: a withdrawal control that disappears on a phone is
 *   not available to the visitor, and a dropdown two presses deep is what this replaces. It is kept
 *   small and quiet instead, and shrinks by 4px below the phone breakpoint.
 * - **44px, so the touch target is a real one** (WCAG 2.5.8 wants 24px, the platform guidelines 44),
 *   with the glyph sized independently so the box does not shrink with it.
 */
export const LauncherButton = styled.button`
	position: fixed;
	left: 16px;
	bottom: 16px;
	z-index: ${zIndex.nav};

	display: flex;
	align-items: center;
	justify-content: center;
	width: 44px;
	height: 44px;
	padding: 0;
	border: 1px solid ${({theme}) => theme.tokens.cardBorder};
	border-radius: ${radii.pill};
	background: ${({theme}) => theme.tokens.card};
	color: ${({theme}) => theme.tokens.ink2};
	cursor: pointer;
	box-shadow: ${({theme}) => theme.tokens.shadowCard};
	transition: color ${motion.hover} ${motion.easeOut}, border-color ${motion.hover} ${motion.easeOut};

	&:hover {
		color: ${({theme}) => theme.tokens.ink};
		border-color: ${({theme}) => theme.tokens.ink2};
	}

	&:focus-visible {
		outline: 2px solid ${({theme}) => theme.tokens.ring};
		outline-offset: 2px;
	}

	@media (prefers-reduced-motion: reduce) {
		transition: none;
	}

	/* Only the INSET tightens on a phone. The box stays 44px: public-navigation.spec.ts holds every
	   visible public control to 44x44 at 375px, and a withdrawal control is not the one to shrink.
	   (No backticks in here: inside a styled-components template literal one would end the CSS.) */
	@media (max-width: 480px) {
		left: 12px;
		bottom: 12px;
	}

	/* A floating control is chrome, and chrome does not belong on a printed cookie policy. */
	@media print {
		display: none;
	}
`;
